/**
 * Per-lesson progress, remembered in the viewer's browser (localStorage). Nothing here is required: every storage
 * access is guarded, a private window or blocked storage simply starts with no progress, and a corrupt value is
 * ignored. Pure functions plus a tiny store over an injected storage, so it is unit-tested without a DOM.
 */
import { clampStep } from './tutorialsModel';

export const PROGRESS_KEY = 'hangar.tutorials.progress.v1';

export interface LessonProgress {
  /** Keys of the steps the viewer reached. */
  seen: string[];
  /** Index of the last step viewed ("continue where you left off"). */
  last: number;
  done: boolean;
}
export type ProgressMap = Readonly<Record<string, LessonProgress>>;
export type ProgressStatus = 'new' | 'started' | 'done';

interface LessonShape {
  id: string;
  steps: readonly { key: string }[];
}

const EMPTY: ProgressMap = Object.freeze({});

function entryOf(value: unknown): LessonProgress | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const seen = Array.isArray(v.seen) ? v.seen.filter((s): s is string => typeof s === 'string') : [];
  const last = typeof v.last === 'number' && Number.isFinite(v.last) ? Math.max(0, Math.trunc(v.last)) : 0;
  return { seen, last, done: v.done === true };
}

/** The stored map; anything unreadable is dropped entry by entry (or entirely when it is not JSON). */
export function parseProgress(raw: string | null | undefined): ProgressMap {
  if (!raw) return EMPTY;
  try {
    const doc: unknown = JSON.parse(raw);
    if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return EMPTY;
    const out: Record<string, LessonProgress> = {};
    for (const [id, value] of Object.entries(doc)) {
      const entry = entryOf(value);
      if (entry) out[id] = entry;
    }
    return out;
  } catch {
    return EMPTY;
  }
}

/** Records that step `index` was reached; the lesson is done once every current step was seen. Same map when unchanged. */
export function markSeen(map: ProgressMap, lesson: LessonShape, index: number): ProgressMap {
  const step = lesson.steps[index];
  if (!step) return map;
  const entry = map[lesson.id] ?? { seen: [], last: 0, done: false };
  const seen = entry.seen.includes(step.key) ? entry.seen : [...entry.seen, step.key];
  const done = entry.done || lesson.steps.every((s) => seen.includes(s.key));
  if (seen === entry.seen && entry.last === index && entry.done === done) return map;
  return { ...map, [lesson.id]: { seen, last: index, done } };
}

/** The lesson played to its end. */
export function markDone(map: ProgressMap, lesson: LessonShape): ProgressMap {
  const entry = map[lesson.id];
  if (entry?.done && entry.seen.length >= lesson.steps.length) return map;
  return { ...map, [lesson.id]: { seen: lesson.steps.map((s) => s.key), last: Math.max(0, lesson.steps.length - 1), done: true } };
}

/** 0–100: the share of the lesson's current steps that were seen (a republished lesson may have new ones). */
export function percentOf(map: ProgressMap, lesson: LessonShape): number {
  const entry = map[lesson.id];
  const total = lesson.steps.length;
  if (!entry || total === 0) return 0;
  if (entry.done) return 100;
  const seen = lesson.steps.filter((s) => entry.seen.includes(s.key)).length;
  return Math.min(100, Math.round((seen / total) * 100));
}

export function statusOf(map: ProgressMap, lesson: LessonShape): ProgressStatus {
  const percent = percentOf(map, lesson);
  return percent >= 100 ? 'done' : percent > 0 ? 'started' : 'new';
}

/** Where the viewer opens a lesson: the last step viewed, or the start for a new or finished lesson. */
export function resumeIndex(map: ProgressMap, lesson: LessonShape): number {
  const entry = map[lesson.id];
  if (!entry || entry.done) return 0;
  return clampStep(entry.last, lesson.steps.length);
}

/* ---------- the store (one per page load, shared by the list and the player) ---------- */

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface ProgressStore {
  read(): ProgressMap;
  update(change: (map: ProgressMap) => ProgressMap): void;
  subscribe(listener: () => void): () => void;
}

/** A store over `storage()` (which may itself throw or return null): reads once, writes through, never throws. */
export function createProgressStore(storage: () => KeyValueStorage | null): ProgressStore {
  let cache: ProgressMap | null = null;
  const listeners = new Set<() => void>();
  const read = (): ProgressMap => {
    if (cache) return cache;
    try {
      cache = parseProgress(storage()?.getItem(PROGRESS_KEY));
    } catch {
      cache = EMPTY;
    }
    return cache;
  };
  const update = (change: (map: ProgressMap) => ProgressMap) => {
    const current = read();
    const next = change(current);
    if (next === current) return;
    cache = next;
    try {
      storage()?.setItem(PROGRESS_KEY, JSON.stringify(next));
    } catch {
      /* quota, private mode or blocked storage: progress lives for this page load only */
    }
    for (const l of listeners) l();
  };
  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => void listeners.delete(listener);
  };
  return { read, update, subscribe };
}
